import { useFixingIndices } from '@src/providers/FixingIndexProvider';
import {
  Box,
  Button,
  ButtonVariants,
  DateTimePicker,
  Field,
  formattedDateForSubscription,
  FormGroup,
  HStack,
  NumberInput,
  parseDate,
  request,
  SearchSelect,
  Stack,
  Text,
  useEndpointsContext,
  useGlobalToasts,
  type FixingIndex,
  type FixingIndexMarkMessageData,
} from '@talos/kyoko';
import { format } from 'date-fns';
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import { useCallback, useMemo, useState } from 'react';

function getFixingIndexLabel(index: FixingIndex) {
  return index.DisplayName ?? index.Name;
}

function getFixingIndexDescription(index: FixingIndex) {
  return `${index.Name} (${index.IndexID})`;
}

function getTimezoneLabel(timezone?: string): string {
  if (!timezone) {
    return '-';
  }

  return timezone;
}

export function IndexMarksEditDrawer({
  initialIndexMark,
  onClose,
}: {
  initialIndexMark?: FixingIndexMarkMessageData;
  onClose: () => void;
}) {
  const { orgApiEndpoint } = useEndpointsContext();
  const { add: addToast } = useGlobalToasts();
  const { fixingIndicesList, fixingIndicesByIndexID } = useFixingIndices();
  const isEditMode = initialIndexMark != null;

  const initialFixingIndex = initialIndexMark ? fixingIndicesByIndexID.get(initialIndexMark.IndexID) : undefined;

  const [indexIDField, setIndexIDField] = useState(
    () =>
      new Field<string>({
        name: 'Fixing Index',
        isRequired: true,
        value: initialIndexMark?.IndexID,
      })
  );

  const [initialZonedMarkTime] = useState(() => {
    if (!initialIndexMark || !initialFixingIndex?.NominalIndexTimeZone) {
      return undefined;
    }

    return utcToZonedTime(initialIndexMark?.NominalTime, initialFixingIndex?.NominalIndexTimeZone);
  });

  const [nominalTimeField, setNominalTimeField] = useState(
    () =>
      new Field<Date | null>({
        name: 'Nominal Time',
        isRequired: true,
        value: parseDate(initialZonedMarkTime) ?? null,
      })
  );

  const [markField, setMarkField] = useState(
    () =>
      new Field<string>({
        name: 'Mark',
        isRequired: true,
        value: initialIndexMark?.Mark,
      })
  );

  const [isSubmitting, setIsSubmitting] = useState(false);
  const fields = useMemo(
    () => [indexIDField, nominalTimeField, markField],
    [indexIDField, nominalTimeField, markField]
  );

  const invalid = useMemo(() => fields.some(field => field.hasInvalidValue), [fields]);

  const fixingIndexOptions = useMemo(
    () =>
      fixingIndicesList.toSorted((a, b) => {
        return (a.DisplayName ?? a.Name).localeCompare(b.DisplayName ?? b.Name);
      }),
    [fixingIndicesList]
  );

  const selectedFixingIndex = useMemo(
    () => fixingIndexOptions.find(option => option.IndexID === indexIDField.value),
    [fixingIndexOptions, indexIDField.value]
  );

  const onSubmit = useCallback(async () => {
    if (isSubmitting) {
      return;
    }

    const indexID = indexIDField.value;
    const timezone = selectedFixingIndex?.NominalIndexTimeZone;
    const mark = markField.value?.trim();
    const nominalTime = nominalTimeField.value;

    if (!indexID || !timezone || !mark || !nominalTime) {
      return;
    }

    // Format to second precision (to drop millis), they aren't relevant
    const nominalTimeUTCDate: Date = zonedTimeToUtc(format(nominalTime, "yyyy-MM-dd'T'HH:mm:ss"), timezone);
    const nominalTimeUTCString = formattedDateForSubscription(nominalTimeUTCDate);

    setIsSubmitting(true);
    const url = `${orgApiEndpoint}/index-marks`;
    const body = isEditMode
      ? {
          ...initialIndexMark,
          Mark: mark, // when editing, you just edit the mark. Everything else stays the same.
        }
      : {
          IndexID: indexID,
          NominalTime: nominalTimeUTCString,
          Mark: mark,
        };

    try {
      await request(isEditMode ? 'PUT' : 'POST', url, body);
      addToast({
        variant: 'POSITIVE',
        text: `Index mark ${isEditMode ? 'updated' : 'created'} successfully.`,
      });
      onClose();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unexpected error';
      addToast({
        variant: 'NEGATIVE',
        text: `Failed to ${isEditMode ? 'update' : 'create'} index mark: ${errorMessage}`,
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [
    addToast,
    indexIDField,
    initialIndexMark,
    isEditMode,
    isSubmitting,
    markField,
    nominalTimeField,
    onClose,
    orgApiEndpoint,
    selectedFixingIndex?.NominalIndexTimeZone,
  ]);

  const handleFixingIndexChange = useCallback(
    (index?: FixingIndex) => {
      setIndexIDField(indexIDField.updateValue(index?.IndexID).setTouched(true));
    },
    [indexIDField]
  );

  const handleNominalTimeChange = useCallback(
    (value: Date | null) => {
      setNominalTimeField(nominalTimeField.updateValue(value).setTouched(true));
    },
    [nominalTimeField]
  );

  const handleMarkChange = useCallback(
    (value?: string) => {
      setMarkField(markField.updateValue(value).setTouched(true));
    },
    [markField]
  );

  const touchAllFields = useCallback(() => {
    setIndexIDField(currentField => currentField.setTouched(true));
    setNominalTimeField(currentField => currentField.setTouched(true));
    setMarkField(currentField => currentField.setTouched(true));
  }, []);

  return (
    <>
      <Box p="spacing16" data-testid="index-mark-drawer-content">
        <FormGroup label="Fixing Index" error={indexIDField.errorString} required={true} mb="spacing16">
          <SearchSelect<FixingIndex>
            data-testid="index-mark-drawer-index-select"
            selection={selectedFixingIndex}
            options={fixingIndexOptions}
            getLabel={getFixingIndexLabel}
            getDescription={getFixingIndexDescription}
            onChange={handleFixingIndexChange}
            disabled={isEditMode || isSubmitting}
            showClear={!isEditMode}
          />
        </FormGroup>

        <HStack w="100%" gap="spacing8">
          <FormGroup
            label="Nominal Time (Index Time Zone)"
            error={nominalTimeField.errorString}
            required={true}
            mb="spacing16"
            flexBasis="55%"
          >
            <DateTimePicker
              data-testid="index-mark-drawer-nominal-time-input"
              value={nominalTimeField.value ?? null}
              onChange={handleNominalTimeChange}
              onBlur={() => setNominalTimeField(currentField => currentField.setTouched(true))}
              disabled={isEditMode || isSubmitting}
              invalid={nominalTimeField.hasInvalidValue}
              touched={nominalTimeField.isTouched}
              placeholder="yyyy-MM-dd HH:mm:ss"
              showShortcuts={false}
            />
          </FormGroup>

          <Stack flexBasis="45%" alignSelf="flex-start" gap="spacing10">
            <Text color="colorTextSubtle" fontSize="fontSizeSm">
              Index Time Zone
            </Text>
            <Text color="colorTextImportant">{getTimezoneLabel(selectedFixingIndex?.NominalIndexTimeZone)}</Text>
          </Stack>
        </HStack>

        <FormGroup label="Mark" error={markField.errorString} required={true}>
          <NumberInput
            data-testid="index-mark-drawer-mark-input"
            value={markField.value ?? ''}
            onChange={handleMarkChange}
            disabled={isSubmitting}
          />
        </FormGroup>

        <HStack justifyContent="flex-end" gap="spacing8" mt="spacing24">
          <Button onClick={onClose} data-testid="index-mark-drawer-close-button">
            Close
          </Button>
          <span onMouseEnter={touchAllFields}>
            <Button
              onClick={onSubmit}
              variant={ButtonVariants.Primary}
              disabled={invalid || isSubmitting}
              data-testid="index-mark-drawer-submit-button"
            >
              {isEditMode ? 'Update' : 'Create'}
            </Button>
          </span>
        </HStack>
      </Box>
    </>
  );
}
